import { useEffect, useRef, useState } from 'react';

import { UpdateRegistrationSettings } from 'connection/create-event';
import { loadWorkingEvent } from 'store/actions/admin/create-event';
import { useAppDispatch, useTypedSelector } from 'store/reducers/use-typed-selector';
import { selectChannelFlag, selectCurrentChannel } from "@/store/selectors/channel-flags";
import { ChannelFeaturesEnum } from "@/types/working-model";

import narrowRegistrationToActiveGate, {
	RegistrationWithGatePointer,
} from './utils/registration/narrow-registration-to-active-gate';

/**
 * One-time, on-load migration for events that were last edited in brandlive-platform and are now being opened
 * in legacy events-3 (the launch flag is OFF). brandlive keeps EVERY configured gate in `requirements` and only
 * collapses to the single active `project_gate_type` at publish — but events-3 treats every `requirements` entry
 * as an active gate, so it would show/enforce all of them at once. Here we collapse the working event to the one
 * gate `project_gate_type` names, drop the platform-only pointer, save, and reload — leaving a clean legacy
 * single-gate event.
 *
 * Guarded to run ONLY when the channel is not on the new platform: `userChannelType` is flag-gated server-side
 * (events-api channel-state.ts — only Webinar/Townhall when webinarplus_launch/brandtv_launch is enabled, else
 * 'unset'). When the flag is ON, brandlive owns `project_gate_type`, so we must not touch it.
 */
const useMigrateRegistrationGating = (isAdmin?: boolean): boolean => {
	const eventUuid = useTypedSelector(state => state.CreateEventReducer.workingEvent?.uuid);
	const registrationSettings = useTypedSelector(state => state.CreateEventReducer.workingEvent?.registration_settings);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const brandTvLaunchOn = useTypedSelector(selectChannelFlag(ChannelFeaturesEnum.brandtv_launch));
	const webinarPlusLaunchOn = useTypedSelector(selectChannelFlag(ChannelFeaturesEnum.webinarplus_launch));
	const currentChannel = useTypedSelector(selectCurrentChannel);

	const [migrating, setMigrating] = useState(false);
	const dispatch = useAppDispatch();

	// Track which event we've already migrated so opening several events in one session each get a single attempt.
	const attemptedUuid = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!currentChannel) return;
		const flagOff = !brandTvLaunchOn && !webinarPlusLaunchOn;
		const pointer = (registrationSettings as RegistrationWithGatePointer | undefined)?.project_gate_type;
		// pointer !== undefined => brandlive-platform authored this gating (a gate, or null = "open").
		// undefined => genuine legacy events-3 event; leave it untouched.
		const needsMigration = !!registrationSettings && pointer !== undefined;
		const alreadyAttempted = attemptedUuid.current === eventUuid;

		const shouldUpdate = isAdmin && flagOff && needsMigration && !alreadyAttempted;
		const canUpdate = token && eventUuid;

		if (shouldUpdate && canUpdate) {
			attemptedUuid.current = eventUuid;
			setMigrating(true);

			(async () => {
				try {
					const narrowed = narrowRegistrationToActiveGate(registrationSettings as RegistrationWithGatePointer);
					await UpdateRegistrationSettings(token, eventUuid, narrowed);
					// Reload so the store/editor reflect the cleaned single-gate data (the pointer is now gone, so this
					// reload does not re-trigger the migration).
					dispatch(loadWorkingEvent(eventUuid, token));
					setMigrating(false);
				} catch (error) {
					// Allow a retry on the next load if the save failed.
					attemptedUuid.current = undefined;
					setMigrating(false);
					console.error('Error migrating registration gating settings', error);
				}
			})();
		}
	}, [dispatch, isAdmin, token, eventUuid, registrationSettings, brandTvLaunchOn, webinarPlusLaunchOn, currentChannel]);

	return migrating;
};

export default useMigrateRegistrationGating;
